import { useEffect, useState, HTMLAttributes, ReactElement } from 'react';
import { assetUrl, cn } from '@Shared/Helpers/Utilities';

const svgCache = new Map<string, string>();
const pendingRequests = new Map<string, Promise<string>>();

interface BaseSvgImageProps extends HTMLAttributes<any> {
    testid: string;
    icon: string;
    path: string;
    className?: string | null;
}

const BaseSvgImage = ({
    className = null,
    testid,
    icon,
    path,
    ...rest
}: BaseSvgImageProps): ReactElement => {
    const [svgContent, setSvgContent] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;

        const loadSvg = async (): Promise<void> => {
            try {
                setIsLoading(true);

                if (svgCache.has(icon)) {
                    if (isMounted) {
                        setSvgContent(svgCache.get(icon) || '');
                        setError(null);
                        setIsLoading(false);
                    }
                    return;
                }

                if (pendingRequests.has(icon)) {
                    const svgText = await pendingRequests.get(icon)!;
                    if (isMounted) {
                        setSvgContent(svgText);
                        setError(null);
                        setIsLoading(false);
                    }
                    return;
                }

                const fetchPromise = (async () => {
                    const svgPath = assetUrl(`${path}/${icon}.svg`);
                    const response = await fetch(svgPath);

                    if (!response.ok) {
                        throw new Error(`Failed to load SVG: ${response.statusText}`);
                    }

                    const svgText = await response.text();
                    svgCache.set(icon, svgText);
                    pendingRequests.delete(icon);
                    return svgText;
                })();

                pendingRequests.set(icon, fetchPromise);

                const svgText = await fetchPromise;

                if (isMounted) {
                    setSvgContent(svgText);
                    setError(null);
                    setIsLoading(false);
                }
            } catch (err) {
                console.error('Error loading SVG:', err);
                pendingRequests.delete(icon);

                if (isMounted) {
                    setError(`Could not load icon: ${icon}`);
                    setIsLoading(false);
                }
            }
        };

        if (icon) {
            loadSvg();
        }

        return () => {
            isMounted = false;
        };
    }, [icon, path]);

    const createMarkup = (): { __html: string } => {
        return { __html: svgContent };
    };

    if (isLoading) {
        return <div className={cn('size-6', className)} />;
    }

    if (error) {
        return <div className={cn('size-6', className)}>{error}</div>;
    }

    return (
        <span
            data-testid={testid}
            className={cn('size-6', className)}
            {...rest}
            dangerouslySetInnerHTML={createMarkup()}
        />
    );
};

export default BaseSvgImage;
